import i18nFactory from '@yandex-int/i18n';

import { LogHelpers } from '../../../service/LogHelpers/LogHelpers';
import { TimeTargetingHelpers } from '../../../service/TimeTargetingHelpers/TimeTargetingHelpers';
import { TimeTargetingDay } from '../../../typings/common';
import { TimeTargetFragment } from '../../../typings/graphql-log';
import {
    ChangedOptions,
    TimeTargetCoefficientsDiff,
    TimeTargetGroups,
    TimeTargetingCorrection,
    TimeTargetingDayDisplay,
    TimeTargetingHolidays,
    TimeTargetingHours
} from '../../../typings/log';

import * as keyset from './timetargeting.i18n';

const i18n = i18nFactory(keyset);

export function prepareTimeTargetingGroups(before?: TimeTargetFragment, after?: TimeTargetFragment): TimeTargetGroups {
    if (!before || !after) {
        throw Error('Некорректное значение временного таргетинга: нет данных');
    }
    const days = Object.keys(TimeTargetingDay),
        isNew = (before.timeZone.id || '0').toString() === '0',
        oldWW = before && before.workingWeekend[0] !== 100, // 0 = учитывать выходные
        newWW = after.workingWeekend[0] !== 100, // 100 = не учитывать
        res: TimeTargetGroups = {
            timezone: {
                id: (after.timeZone.id || '0').toString(),
                name: after.timeZone.name
            },
            oldTimezone: {
                id: (before.timeZone.id || '0').toString(),
                name: before.timeZone.name
            },
            workingWeekends: {
                oldWW,
                newWW
            },
            holidays: {
                time: [],
                oldCorrection: LogHelpers.noCorrectionValue,
                newCorrection: LogHelpers.noCorrectionValue,
                correctionChangeType: ChangedOptions.notModified
            },
            time: [],
            corrections: [],
            needExpand: true,
            alwaysNeedExpand: true,
            maxLineLength: 0
        },
        diffHoursMap = new Map<TimeTargetingDay, Array<TimeTargetingHours>>(),
        diffCorrectionsMap = new Map<TimeTargetingDay, Array<TimeTargetingCorrection>>();

    days.forEach((day: string) => {
        const diff: Array<TimeTargetCoefficientsDiff> = [];
        after[day].forEach((newCoef: number, index: number) => {
            const oldCoef = before[day][index] || 0;

            diff.push({ oldCoef: oldCoef, newCoef: newCoef });
        });

        diffHoursMap.set(day as TimeTargetingDay, TimeTargetingHelpers.getTimeRange(diff, isNew));
        diffCorrectionsMap.set(day as TimeTargetingDay, TimeTargetingHelpers.getCorrectionRange(diff, isNew));
    });

    const time = TimeTargetingHelpers.getTimeDayText(diffHoursMap);
    const corrections = TimeTargetingHelpers.getCorrectionsDayText(diffCorrectionsMap);
    const holidays = prepareHolidays(before, after, isNew);

    const alwaysNeedExpand =
        time.length > 2 || // более 2 периодов во времени
        corrections.length > 0 || // есть корректировки
        holidays.time.length > 0 || // есть показы по празникам
        oldWW || // есть показы по рабочим выходным
        newWW;
    const maxLineLength = calcTimeTargetingMaxLength(time, before.timeZone.name, after.timeZone.name || '');
    const needExpand = LogHelpers.calcNeedExpand(alwaysNeedExpand, maxLineLength);

    return {
        ...res,
        needExpand,
        time,
        corrections,
        holidays,
        alwaysNeedExpand,
        maxLineLength
    };
}

function calcTimeTargetingMaxLength(
    time: Array<TimeTargetingDayDisplay>,
    oldTimeZone: string | null,
    newTimeZone: string
): number {
    const timezoneLength =
        oldTimeZone === newTimeZone || oldTimeZone === null
            ? i18n('timezone').length + newTimeZone.length + 2
            : i18n('timezone').length + oldTimeZone.length + newTimeZone.length + 3;
    // длинная строка с периодами
    const timeLength = time.reduce((length: number, entry: TimeTargetingDayDisplay) => {
        length += entry.daysText.length + entry.hours.join(', ').length;
        return length;
    }, 0);

    return Math.max(timeLength, timezoneLength);
}

function prepareHolidays(before: TimeTargetFragment, after: TimeTargetFragment, isNew: boolean): TimeTargetingHolidays {
    const holidayDiff: Array<TimeTargetCoefficientsDiff> = [];
    after.holiday.forEach((newCoef: number, index: number) => {
        const oldCoef = before.holiday[index] || 0;

        holidayDiff.push({ oldCoef: oldCoef, newCoef: newCoef });
    });
    const holidays = TimeTargetingHelpers.getCorrectionRange(holidayDiff, isNew);
    const holidaysTime = TimeTargetingHelpers.getTimeRange(holidayDiff, isNew);
    const holidaysCorrections = TimeTargetingHelpers.getHolidaysCorrections(holidays);

    return {
        ...holidaysCorrections,
        time: holidaysTime
    };
}
